import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  scadaApi, type PumpStatus, type ReservoirStatus, type ScadaDevice, type DeviceCommErrorCategory, type NodeType,
} from '../api/scada'
import { Drawer } from './Drawer'
import { FaultNotice } from './FaultBadge'

// Duplicated from ScadaView.tsx — same "small enough to duplicate locally"
// call this session already made a few times (InfrastructureFocus.tsx's
// own commErrorLabel copy) rather than exporting one-off
// cross-file helpers.
function fmtAgo(iso: string | null): string {
  if (!iso) return 'Nema podataka'
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 5)    return 'Upravo'
  if (s < 60)   return `${s}s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  return `${Math.floor(s / 3600)} h`
}

function fmtCountdown(minutes: number | null): string | null {
  if (minutes === null) return null
  if (minutes === 0)    return '⚠ Kritično'
  if (minutes < 60)     return `${minutes} min`
  const h = Math.floor(minutes / 60), m = minutes % 60
  return m > 0 ? `${h} h ${m} min` : `${h} h`
}

const COMM_ERROR_LABEL: Partial<Record<DeviceCommErrorCategory, string>> = {
  IllegalAddress: 'Nepostojeća adresa',
  no_response:    'Bez odgovora',
}
function commErrorLabel(category: DeviceCommErrorCategory): string {
  return COMM_ERROR_LABEL[category] ?? `Greška: ${category}`
}

function fmtNum(n: number | null, suffix = ''): string {
  return n !== null ? `${n}${suffix}` : '—'
}
function fmtRange(low: number | null, high: number | null): string {
  if (low === null && high === null) return '—'
  return `${low ?? '—'} – ${high ?? '—'}`
}

const NODE_TYPE_TITLE: Record<NodeType, string> = {
  pump:             'Crpna stanica',
  reservoir:        'Rezervoar',
  flow_meter:       'Merač protoka',
  pressure_sensor:  'Senzor pritiska',
  level_sensor:     'Senzor nivoa',
  valve:            'Ventil',
}

// ── Presentation building blocks ────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{title}</p>
      <div className="rounded-lg border border-gray-100 divide-y divide-gray-50">{children}</div>
    </div>
  )
}

function Row({ label, value, valueClassName = 'text-gray-900' }: {
  label: string
  value: string
  valueClassName?: string
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium text-right ${valueClassName}`}>{value}</span>
    </div>
  )
}

// ── Per-type detail bodies ───────────────────────────────────────────────────
//
// Data-model note (flagged, not silently papered over): thresholds are one
// generic warn_low/warn_high/alert_low/alert_high pair per device (plus
// depth-specific fields for reservoirs) -- not independent threshold sets
// per reading (e.g. a pump doesn't have separate pressure vs flow alert
// ranges). This shows exactly what's configured, nothing invented.

function PumpDetail({ pump, device }: { pump: PumpStatus; device: ScadaDevice | undefined }) {
  const statusLabel = pump.active === true ? 'Aktivna' : pump.active === false ? 'Neaktivna' : 'Nepoznato'
  return (
    <div className="space-y-4">
      <Section title="Status">
        <Row label="Stanje" value={statusLabel} />
        <Row label="Ažurirano" value={fmtAgo(pump.recorded_at)} />
        {pump.fault === true && (
          <div className="px-3 py-2"><FaultNotice since={pump.fault_since} critical={pump.status_severity === 'critical'} /></div>
        )}
        {pump.comm_error && (
          <Row label="Greška komunikacije" value={commErrorLabel(pump.comm_error)} valueClassName="text-brand-critical-text" />
        )}
      </Section>
      <Section title="Očitavanja">
        <Row label="Protok" value={pump.flow_lps !== null ? `${pump.flow_lps.toFixed(2)} L/s` : '—'} />
        <Row label="Pritisak" value={pump.pressure_kpa !== null ? `${pump.pressure_kpa.toFixed(1)} kPa` : '—'} />
      </Section>
      {device ? (
        <Section title="Pragovi">
          <Row label="Normalna vrednost" value={fmtNum(device.expected_value)} />
          <Row label="Upozorenje (min–maks)" value={fmtRange(device.warn_low, device.warn_high)} />
          <Row label="Alarm (min–maks)" value={fmtRange(device.alert_low, device.alert_high)} />
          <Row label="Prag alarma — nema signala" value={device.alert_offline_minutes !== null ? `${device.alert_offline_minutes} min` : '—'} />
          <Row label="Prag alarma — pumpa stoji" value={device.alert_stopped_minutes !== null ? `${device.alert_stopped_minutes} min` : '—'} />
        </Section>
      ) : (
        <p className="text-xs text-gray-400">Učitavanje pragova…</p>
      )}
    </div>
  )
}

function ReservoirDetail({ reservoir, device }: { reservoir: ReservoirStatus; device: ScadaDevice | undefined }) {
  const toEmpty = fmtCountdown(reservoir.time_to_empty_minutes)
  const toCritical = fmtCountdown(reservoir.minutes_to_critical)
  return (
    <div className="space-y-4">
      <Section title="Status">
        <Row label="Ažurirano" value={fmtAgo(reservoir.recorded_at)} />
        {reservoir.comm_error && (
          <Row label="Greška komunikacije" value={commErrorLabel(reservoir.comm_error)} valueClassName="text-brand-critical-text" />
        )}
      </Section>
      <Section title="Očitavanja">
        <Row label="Dubina" value={reservoir.depth_m !== null ? `${reservoir.depth_m.toFixed(3)} m` : '—'} />
        <Row label="Zapremina" value={reservoir.volume_m3 !== null ? `${reservoir.volume_m3.toFixed(1)} m³` : '—'} />
        <Row label="Stopa promene" value={reservoir.rate_m_per_h !== null ? `${reservoir.rate_m_per_h.toFixed(3)} m/h` : '—'} />
        {toCritical && <Row label="Do kritičnog nivoa" value={toCritical} />}
        {toEmpty && <Row label="Do pražnjenja" value={toEmpty} />}
      </Section>
      <Section title="Pragovi">
        <Row label="Min. operativna dubina" value={reservoir.critical_depth_m !== null ? `${reservoir.critical_depth_m.toFixed(2)} m` : '—'} />
        <Row label="Upozorenje — dubina" value={reservoir.warning_depth_m !== null ? `${reservoir.warning_depth_m.toFixed(2)} m` : '—'} />
        <Row label="Maks. dubina" value={reservoir.max_depth_m !== null ? `${reservoir.max_depth_m.toFixed(2)} m` : '—'} />
        <Row label="Kapacitet" value={reservoir.capacity_m3 !== null ? `${reservoir.capacity_m3.toFixed(1)} m³` : '—'} />
        <Row label="Prag alarma — vreme do praznog" value={reservoir.tte_alert_threshold !== null ? `${reservoir.tte_alert_threshold} min` : '—'} />
        {device && (
          <>
            <Row label="Upozorenje (min–maks)" value={fmtRange(device.warn_low, device.warn_high)} />
            <Row label="Alarm (min–maks)" value={fmtRange(device.alert_low, device.alert_high)} />
            <Row label="Prag alarma — nema signala" value={device.alert_offline_minutes !== null ? `${device.alert_offline_minutes} min` : '—'} />
          </>
        )}
      </Section>
    </div>
  )
}

// flow_meter/pressure_sensor/level_sensor/valve had no detail view at all
// before this -- the map marker and its popup had nowhere to send "Detalji"
// to for these types (reported live: a pressure sensor's reading was
// nowhere to be found in the UI). latest_value/measurement_unit are the
// same generic-device fields GET /scada/devices already returns for every
// non-pump/reservoir type (api/scada.ts's own field comment); this is the
// first place that actually shows them.
function GenericDeviceDetail({ device }: { device: ScadaDevice }) {
  return (
    <div className="space-y-4">
      <Section title="Status">
        <Row label="Ažurirano" value={fmtAgo(device.latest_recorded_at)} />
      </Section>
      <Section title="Očitavanje">
        <Row
          label="Vrednost"
          value={device.latest_value !== null ? `${device.latest_value.toFixed(2)}${device.measurement_unit ? ` ${device.measurement_unit}` : ''}` : '—'}
          valueClassName={device.severity === 'critical' ? 'text-brand-critical-text' : device.severity === 'warning' ? 'text-brand-warning-text' : 'text-gray-900'}
        />
      </Section>
      <Section title="Pragovi">
        <Row label="Normalna vrednost" value={fmtNum(device.expected_value)} />
        <Row label="Upozorenje (min–maks)" value={fmtRange(device.warn_low, device.warn_high)} />
        <Row label="Alarm (min–maks)" value={fmtRange(device.alert_low, device.alert_high)} />
        <Row label="Prag alarma — nema signala" value={device.alert_offline_minutes !== null ? `${device.alert_offline_minutes} min` : '—'} />
      </Section>
    </div>
  )
}

// ── Extra readings (generic, any device) ────────────────────────────────────
// GET /devices/{device_key}/extra-readings — flat list, no node_type
// awareness needed. Shown for both pump and reservoir, since a device's
// extra readings are configured independently of its native fields.

function ExtraReadingsSection({ deviceKey }: { deviceKey: string }) {
  const { data: readings = [] } = useQuery({
    queryKey: ['scada-extra-readings', deviceKey],
    queryFn: () => scadaApi.getExtraReadings(deviceKey).then((r) => r.data),
    refetchInterval: 30_000,
  })
  if (readings.length === 0) return null
  return (
    <Section title="Dodatna očitavanja">
      {readings.map((r) => (
        <Row key={r.field}
          label={r.sensor_type ? `${r.field} (${r.sensor_type})` : r.field}
          value={`${r.value} · ${fmtAgo(r.recorded_at)}`} />
      ))}
    </Section>
  )
}

// ── Modal shell ───────────────────────────────────────────────────────────────

export function DeviceDetailModal({ deviceKey, nodeType, onClose }: {
  deviceKey: string
  nodeType: NodeType
  onClose: () => void
}) {
  // Same query keys ScadaView.tsx/InfrastructureFocus.tsx already use --
  // reuses their cache, no extra network request in the common case of
  // opening this from a page that already fetched them.
  const { data: latest } = useQuery({
    queryKey: ['scada-latest'],
    queryFn: () => scadaApi.latest().then((r) => r.data),
    refetchInterval: 30_000,
  })
  const { data: devices = [] } = useQuery({
    queryKey: ['scada-devices'],
    queryFn: () => scadaApi.listDevices().then((r) => r.data),
    staleTime: 60_000,
  })

  const device    = devices.find((d) => d.device_key === deviceKey)
  const pump      = nodeType === 'pump' ? latest?.pumps.find((p) => p.device_key === deviceKey) : undefined
  const reservoir = nodeType === 'reservoir' ? latest?.reservoirs.find((r) => r.device_key === deviceKey) : undefined
  const label     = pump?.label ?? reservoir?.label ?? device?.label ?? deviceKey

  return (
    <Drawer
      onClose={onClose}
      title={
        <>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-cyan-text">
            {NODE_TYPE_TITLE[nodeType]}
          </p>
          <h2 className="text-lg font-bold text-gray-900 mt-0.5">{label}</h2>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{deviceKey}</p>
        </>
      }
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        {nodeType === 'pump' ? (
          pump ? <PumpDetail pump={pump} device={device} /> : <p className="text-sm text-gray-400">Učitavanje…</p>
        ) : nodeType === 'reservoir' ? (
          reservoir ? <ReservoirDetail reservoir={reservoir} device={device} /> : <p className="text-sm text-gray-400">Učitavanje…</p>
        ) : (
          device ? <GenericDeviceDetail device={device} /> : <p className="text-sm text-gray-400">Učitavanje…</p>
        )}
        <ExtraReadingsSection deviceKey={deviceKey} />
      </div>
    </Drawer>
  )
}
